import React, { useState } from 'react';
import {
  ErrorOutline,
  KeyboardArrowDown,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { Button, Chip, Menu, MenuItem, Stack } from '@mui/material';
import {
  usePublish,
  useVisibility,
} from '@/data/learning-path/path-management';

type StatusMenuButtonProps = {
  published: boolean;
  hasDraft: boolean;
  hidden: boolean;
  pathId: string;
};

const StatusChip = () => (
  <Chip
    label="Alterações salvas sem publicar"
    variant="outlined"
    size="small"
    color="info"
    icon={<ErrorOutline sx={{ fontSize: '16px' }} />}
    sx={{ width: 'fit-content' }}
  />
);

export const StatusMenuButton = ({
  published,
  hasDraft = false,
  hidden,
  pathId,
}: StatusMenuButtonProps) => {
  const { mutate: publish, isPending: isPublishing } = usePublish();
  const { mutate: toggleVisibility, isPending: isTogglingVisibility } =
    useVisibility();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handlePublish = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    publish(
      { pathId },
      {
        onSettled: () => setAnchorEl(null),
      }
    );
  };

  const handleToggleVisibility = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleVisibility(
      { pathId },
      {
        onSettled: () => setAnchorEl(null),
      }
    );
  };

  const renderActionsByStatus = () => {
    const actions = [];

    if (hasDraft && published) {
      actions.push({
        label: 'Atualizar alterações salvas',
        action: handlePublish,
      });
    }

    if (!published || (published && hidden)) {
      actions.push({
        label: 'Visível',
        action: published ? handleToggleVisibility : handlePublish,
      });
    }

    if (published && !hidden) {
      actions.push({
        label: 'Ocultar',
        action: handleToggleVisibility,
      });
    }

    return actions;
  };

  const statusLabel = published && !hidden ? 'Visível' : 'Oculta';
  const publishedAndVisible = published && !hidden;

  return (
    <Stack spacing={2}>
      {hasDraft && <StatusChip />}
      <Button
        variant="outlined"
        color={publishedAndVisible ? 'success' : 'inherit'}
        sx={{ maxWidth: '120px' }}
        endIcon={<KeyboardArrowDown />}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        disabled={isPublishing || isTogglingVisibility}
      >
        <Stack direction="row" gap={1} alignItems="center">
          {publishedAndVisible ? (
            <VisibilityRounded sx={{ width: '16px', height: '16px' }} />
          ) : (
            <VisibilityOffRounded sx={{ width: '16px', height: '16px' }} />
          )}
          {statusLabel}
        </Stack>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {renderActionsByStatus().map(({ label, action }) => (
          <MenuItem key={label} onClick={action}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};
